import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Tooltip,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
} from "@mui/material";
import InfoTooltip from "src/reusable/components/InfoTooltip";
import { KPI_TOOLTIPS } from "./tooltipConstants";

const ConfusionMatrixTable = ({ matrixData, modelOutputName }) => {
  const matrix = useMemo(() => matrixData?.matrix ?? {}, [matrixData]);
  const classes = useMemo(() => matrixData?.classes ?? [], [matrixData]);
  const total_samples = matrixData?.total_samples ?? 0;
  const isWide = classes.length > 8;
  const cellMinWidth = isWide ? "48px" : "80px";
  const cellFontSize = isWide ? "0.75rem" : undefined;

  const labelStyle = {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    fontSize: cellFontSize,
    whiteSpace: "nowrap",
  };
  const headerStyle = {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    minWidth: cellMinWidth,
    fontSize: cellFontSize,
  };

  const { metrics, overallAccuracy, maxValue } = useMemo(() => {
    const m = {};
    let correct = 0;
    let mv = 0;

    classes.forEach((actualClass) => {
      let tp = 0;
      let fp = 0;
      let fn = 0;

      if (matrix[actualClass]?.[actualClass]) {
        tp = matrix[actualClass][actualClass];
        correct += tp;
      }

      if (matrix[actualClass]) {
        Object.entries(matrix[actualClass]).forEach(([pred, count]) => {
          if (pred !== actualClass) fn += count || 0;
          if ((count || 0) > mv) mv = count;
        });
      }

      Object.keys(matrix).forEach((other) => {
        if (other !== actualClass && matrix[other]) {
          fp += matrix[other][actualClass] || 0;
        }
      });

      const precision = tp / (tp + fp) || 0;
      const recall = tp / (tp + fn) || 0;
      const f1Score = (2 * (precision * recall)) / (precision + recall) || 0;

      m[actualClass] = {
        precision: (precision * 100).toFixed(1),
        recall: (recall * 100).toFixed(1),
        f1Score: f1Score.toFixed(3),
      };
    });

    // pick up maxValue from diagonal cells not already visited
    Object.values(matrix).forEach((row) => {
      Object.values(row).forEach((v) => {
        if (v > mv) mv = v;
      });
    });

    return {
      metrics: m,
      overallAccuracy:
        total_samples > 0 ? ((correct / total_samples) * 100).toFixed(1) : 0,
      maxValue: mv,
    };
  }, [matrix, classes, total_samples]);

  const getCellColor = (value, maxVal, isDiagonal) => {
    if (value === 0 || value === undefined) return "#ffffff";
    const intensity = maxVal > 0 ? value / maxVal : 0;
    if (isDiagonal) {
      const g = Math.floor(255 - intensity * 155);
      return `rgb(${g}, 255, ${g})`;
    }
    const r = Math.floor(255 - intensity * 155);
    return `rgb(255, ${r}, ${r})`;
  };

  if (!classes.length) return null;

  // Stable kebab-case slug of the model output name for the test id
  const outputSlug = modelOutputName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2 }}
      data-testid={`kpi-confusion-matrix-${outputSlug}`}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {modelOutputName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total Samples: {total_samples} | Overall Accuracy: {overallAccuracy}%
        </Typography>
      </Box>
      <CardContent>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={labelStyle}>Actual \ Predicted</TableCell>
                {classes.map((predictedClass) => (
                  <TableCell
                    key={`header-${predictedClass}`}
                    align="center"
                    style={headerStyle}
                  >
                    {predictedClass}
                  </TableCell>
                ))}
                <TableCell
                  style={{
                    fontWeight: "bold",
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #ddd",
                    minWidth: cellMinWidth,
                    fontSize: cellFontSize,
                  }}
                  align="center"
                >
                  Recall %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map((actualClass) => (
                <TableRow key={`row-${actualClass}`}>
                  <TableCell style={labelStyle}>{actualClass}</TableCell>
                  {classes.map((predictedClass) => {
                    const value = matrix[actualClass]
                      ? matrix[actualClass][predictedClass] || 0
                      : 0;
                    const isDiagonal = actualClass === predictedClass;

                    return (
                      <TableCell
                        key={`cell-${actualClass}-${predictedClass}`}
                        align="center"
                        style={{
                          backgroundColor: getCellColor(
                            value,
                            maxValue,
                            isDiagonal,
                          ),
                          border: "1px solid #ddd",
                          fontWeight: isDiagonal ? "bold" : "normal",
                          fontSize: cellFontSize,
                        }}
                      >
                        <Tooltip
                          title={`Actual: ${actualClass}, Predicted: ${predictedClass}`}
                          arrow
                        >
                          <span>{value}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="center"
                    style={{
                      backgroundColor: "#e8f5e9",
                      border: "1px solid #ddd",
                      fontWeight: "bold",
                      fontSize: cellFontSize,
                    }}
                  >
                    {metrics[actualClass].recall}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  style={{
                    fontWeight: "bold",
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #ddd",
                    fontSize: cellFontSize,
                    whiteSpace: "nowrap",
                  }}
                >
                  Precision %
                </TableCell>
                {classes.map((predictedClass) => (
                  <TableCell
                    key={`precision-${predictedClass}`}
                    align="center"
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #ddd",
                      fontSize: cellFontSize,
                    }}
                  >
                    {metrics[predictedClass].precision}
                  </TableCell>
                ))}
                <TableCell
                  style={{
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                  }}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Performance Metrics by Class:
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>Class</TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    Precision %
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    Recall %
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    F1 Score
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((className) => (
                  <TableRow key={`metrics-${className}`}>
                    <TableCell style={{ fontWeight: "bold" }}>
                      {className}
                    </TableCell>
                    <TableCell align="center">
                      {metrics[className].precision}
                    </TableCell>
                    <TableCell align="center">
                      {metrics[className].recall}
                    </TableCell>
                    <TableCell align="center">
                      {metrics[className].f1Score}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

ConfusionMatrixTable.propTypes = {
  matrixData: PropTypes.shape({
    matrix: PropTypes.object.isRequired,
    classes: PropTypes.arrayOf(PropTypes.string).isRequired,
    total_samples: PropTypes.number.isRequired,
  }).isRequired,
  modelOutputName: PropTypes.string.isRequired,
};

const SUBSET_LABELS = {
  all: "All Records",
  automated: "Automated",
  manual_sample: "Manual Sample",
};

const ConfusionMatrixSubset = ({ matrices }) => {
  if (!matrices || Object.keys(matrices).length === 0) {
    return (
      <Typography
        color="text.secondary"
        sx={{ py: 2 }}
        data-testid="kpi-confusion-empty"
      >
        No confusion matrix data available for this subset.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {Object.entries(matrices).map(([modelOutputName, matrixData]) => (
        <Grid item xs={12} key={modelOutputName}>
          <ConfusionMatrixTable
            matrixData={matrixData}
            modelOutputName={modelOutputName}
          />
        </Grid>
      ))}
    </Grid>
  );
};

ConfusionMatrixSubset.propTypes = {
  matrices: PropTypes.object,
};

export const ConfusionMatrixSection = ({ confusionMatrices }) => {
  const [tabIdx, setTabIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Reset tab when available subsets change (e.g., new data loaded)
  useEffect(() => {
    setTabIdx(0);
  }, [confusionMatrices]);

  if (!confusionMatrices || Object.keys(confusionMatrices).length === 0) {
    return null;
  }

  // Available subsets (only show tabs that have data)
  const subsets = Object.keys(SUBSET_LABELS).filter(
    (key) => confusionMatrices[key],
  );

  if (subsets.length === 0) return null;

  const activeSubset = subsets[Math.min(tabIdx, subsets.length - 1)];

  return (
    <Box mt={4} data-testid="kpi-confusion-matrices">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "20px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h5">Confusion Matrices</Typography>
          <InfoTooltip title={KPI_TOOLTIPS.confusionMatrices} />
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={expanded}
              onChange={(e) => setExpanded(e.target.checked)}
              data-testid="kpi-confusion-toggle"
              data-attr="cap-ai:toggle:confusion-matrices"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Show confusion matrices
            </Typography>
          }
        />
      </Box>
      {expanded && (
        <>
          {subsets.length > 1 && (
            <Tabs
              value={Math.min(tabIdx, subsets.length - 1)}
              onChange={(_, v) => setTabIdx(v)}
              aria-label="Confusion matrix subsets"
              sx={{ mb: 2 }}
              data-testid="kpi-confusion-tabs"
            >
              {subsets.map((key) => (
                <Tab
                  key={key}
                  label={SUBSET_LABELS[key] || key}
                  data-testid={`kpi-confusion-tab-${key}`}
                  data-attr={`cap-ai:tab:confusion-matrix-${key}`}
                />
              ))}
            </Tabs>
          )}
          <ConfusionMatrixSubset matrices={confusionMatrices[activeSubset]} />
        </>
      )}
    </Box>
  );
};

ConfusionMatrixSection.propTypes = {
  confusionMatrices: PropTypes.object,
};

export default ConfusionMatrixSection;
